// Tabs
// Groups the tab triggers rendered by each `_tab` block into a single tablist,
// then shows one panel at a time. The first tab is active by default.
class TabsComponent extends HTMLElement {
  #controller = new AbortController();

  connectedCallback() {
    const { signal } = this.#controller;

    this.#build();

    this.addEventListener('click', this.#handleClick, { signal });
    this.addEventListener('keydown', this.#handleKeyDown, { signal });

    // In the theme editor, reveal the relevant tab when it (or a block inside it) is selected.
    if (window.Shopify?.designMode) {
      document.addEventListener('shopify:block:select', this.#handleBlockSelect, { signal });
    }
  }

  disconnectedCallback() {
    this.#controller.abort();
  }

  /** @returns {HTMLElement[]} */
  get triggers() {
    return Array.from(this.querySelectorAll(':scope .tabs__tab-trigger'));
  }

  /** @returns {HTMLElement[]} */
  get panels() {
    return Array.from(this.querySelectorAll(':scope > .tabs__panel'));
  }

  /**
   * Removes a tab — its trigger, its panel, or whichever of the two is still
   * here — and puts the component back into a consistent state. For a set that
   * can only find out after load that it has nothing to show: the Product
   * selection widget's recently-viewed tab, when the shopper has no history.
   *
   * A switch down to its last tab stops being a switch. The panels drop the
   * tab/tabpanel roles a lone trigger can no longer justify — the same pair the
   * blocks leave off when they know up front there is only one set — and
   * `data-single-tab` states the fact for the section's own CSS to hide the list
   * with, rather than this file naming a class that belongs to one of its
   * consumers. Otherwise the tab left behind is activated, in case the one that
   * went was the selected one.
   *
   * Safe to call for a tab that has already taken itself out of the DOM, and
   * safe to call twice.
   *
   * @param {string} [tabId]
   */
  removeTab(tabId) {
    if (tabId) {
      for (const element of this.querySelectorAll(`[data-tab-id="${CSS.escape(tabId)}"]`)) {
        element.remove();
      }
    }

    const triggers = this.triggers;

    this.toggleAttribute('data-single-tab', triggers.length < 2);

    if (triggers.length > 1) {
      this.#build();
      return;
    }

    for (const panel of this.panels) {
      panel.removeAttribute('role');
      panel.removeAttribute('aria-labelledby');
      panel.classList.add('tabs__panel--active');
      panel.hidden = false;
    }
  }

  /**
   * Moves the tab triggers into a shared tablist and activates the first tab.
   */
  #build() {
    const triggers = this.triggers;
    if (triggers.length === 0) return;

    let list = this.querySelector(':scope > .tabs__list');
    if (!list) {
      list = document.createElement('div');
      list.className = 'tabs__list';
      list.setAttribute('role', 'tablist');
      this.prepend(list);
    }

    for (const trigger of triggers) {
      list.appendChild(trigger);
    }

    const active = triggers.find((trigger) => trigger.getAttribute('aria-selected') === 'true') ?? triggers[0];
    this.#activate(active, false);

    // Stated here as well as in `removeTab`, because a tab can take itself off
    // the widget before this component has been upgraded — the shortened list is
    // then simply what the first pass reads.
    this.toggleAttribute('data-single-tab', triggers.length < 2);

    this.setAttribute('data-ready', '');
  }

  /**
   * @param {Event} event
   */
  #handleClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const trigger = event.target.closest('.tabs__tab-trigger');
    if (!(trigger instanceof HTMLElement) || !this.contains(trigger)) return;

    event.preventDefault();
    this.#activate(trigger);
  };

  /**
   * Arrow key navigation following the ARIA tabs pattern.
   * @param {KeyboardEvent} event
   */
  #handleKeyDown = (event) => {
    if (!(event.target instanceof Element)) return;

    const trigger = event.target.closest('.tabs__tab-trigger');
    if (!(trigger instanceof HTMLElement)) return;

    const triggers = this.triggers;
    const index = triggers.indexOf(trigger);
    if (index === -1) return;

    let next;
    switch (event.key) {
      case 'ArrowRight':
        next = triggers[(index + 1) % triggers.length];
        break;
      case 'ArrowLeft':
        next = triggers[(index - 1 + triggers.length) % triggers.length];
        break;
      case 'Home':
        next = triggers[0];
        break;
      case 'End':
        next = triggers[triggers.length - 1];
        break;
      default:
        return;
    }

    event.preventDefault();
    this.#activate(next);
    next.focus();
  };

  /**
   * Reveals the tab a selected editor block belongs to.
   * @param {Event} event
   */
  #handleBlockSelect = (event) => {
    if (!(event.target instanceof Element) || !this.contains(event.target)) return;

    const trigger = event.target.closest('.tabs__tab-trigger');
    if (trigger instanceof HTMLElement) {
      this.#activate(trigger, false);
      return;
    }

    const panel = event.target.closest('.tabs__panel');
    if (!(panel instanceof HTMLElement)) return;

    const match = this.triggers.find((item) => item.dataset.tabId === panel.dataset.tabId);
    if (match) this.#activate(match, false);
  };

  /**
   * Selects a trigger and reveals its matching panel.
   * @param {HTMLElement} trigger
   * @param {boolean} [focus]
   */
  #activate(trigger, focus = true) {
    const tabId = trigger.dataset.tabId;

    for (const item of this.triggers) {
      const selected = item === trigger;
      item.classList.toggle('tab--selected', selected);
      item.setAttribute('aria-selected', selected ? 'true' : 'false');
      item.tabIndex = selected ? 0 : -1;
    }

    for (const panel of this.panels) {
      const selected = panel.dataset.tabId === tabId;
      panel.classList.toggle('tabs__panel--active', selected);
      panel.hidden = !selected;
    }

    if (focus) trigger.focus();
  }
}

if (!customElements.get('tabs-component')) {
  customElements.define('tabs-component', TabsComponent);
}
